import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import MessageThread from "@/components/MessageThread";
import MessageComposer from "@/components/MessageComposer";
import { Megaphone, ArrowLeft, Paperclip, X, Upload } from "lucide-react";

const initials = (c) => `${(c.first_name || "")[0] || ""}${(c.last_name || "")[0] || ""}`.toUpperCase();

export default function CoachMessages() {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // learner_id
  const [messages, setMessages] = useState([]);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const threadEndScroll = useRef();

  const loadConvs = () => api.get("/coach/conversations").then(({ data }) => setConvs(data));
  useEffect(() => { loadConvs(); }, []);

  const openConv = async (learnerId) => {
    setActive(learnerId);
    const { data } = await api.get(`/coach/conversations/${learnerId}/messages`);
    setMessages(data);
    loadConvs();
  };

  useEffect(() => { threadEndScroll.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (payload) => {
    await api.post(`/coach/conversations/${active}/messages`, payload);
    const { data } = await api.get(`/coach/conversations/${active}/messages`);
    setMessages(data);
    loadConvs();
  };

  const activeConv = convs.find((c) => c.learner_id === active);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif-accent text-3xl sm:text-4xl">Messagerie</h1>
          <p className="text-[#5C6561] mt-1">Échangez avec vos apprenants, individuellement ou tous à la fois.</p>
        </div>
        <BroadcastDialog open={broadcastOpen} setOpen={setBroadcastOpen} onDone={loadConvs} />
      </div>

      <div className="dec-card overflow-hidden grid md:grid-cols-[320px_1fr] h-[70vh]">
        {/* Conversation list */}
        <div className={`${active ? "hidden md:block" : "block"} border-r border-[#E6E1DA] overflow-y-auto`} data-testid="conversation-list">
          {convs.length === 0 && <div className="p-6 text-sm text-[#5C6561]">Aucun apprenant.</div>}
          {convs.map((c) => (
            <button
              key={c.learner_id}
              onClick={() => openConv(c.learner_id)}
              className={`w-full text-left flex items-center gap-3 px-4 py-3 border-b border-[#F0EBE4] hover:bg-[#FAF8F5] ${active === c.learner_id ? "bg-[#FDF0EC]" : ""}`}
              data-testid={`conversation-${c.learner_id}`}
            >
              <div className="w-10 h-10 rounded-full bg-[#F4EFEA] text-[#D95D39] flex items-center justify-center font-semibold text-sm shrink-0">{initials(c) || "?"}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{c.first_name} {c.last_name}</div>
                <div className="text-xs text-[#5C6561] truncate">
                  {c.last_message ? ((c.last_message.sender === "coach" ? "Vous : " : "") + (c.last_message.text || "📎 Pièce jointe")) : "Démarrer la conversation"}
                </div>
              </div>
              {c.unread_count > 0 && (
                <span className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-[#D95D39] text-white text-[11px] font-semibold flex items-center justify-center" data-testid={`conv-unread-${c.learner_id}`}>{c.unread_count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Thread */}
        <div className={`${active ? "flex" : "hidden md:flex"} flex-col min-h-0`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-[#5C6561] text-sm">Sélectionnez une conversation.</div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E6E1DA] bg-white">
                <button className="md:hidden" onClick={() => setActive(null)} data-testid="conv-back"><ArrowLeft className="w-5 h-5" /></button>
                <div className="font-semibold">{activeConv?.first_name} {activeConv?.last_name}</div>
              </div>
              <MessageThread messages={messages} me="coach" />
              <div ref={threadEndScroll} />
              <MessageComposer uploadPath="/coach/upload" onSend={send} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BroadcastDialog({ open, setOpen, onDone }) {
  const [text, setText] = useState("");
  const [att, setAtt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/coach/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = f.name.toLowerCase(); const t = (f.type || "").toLowerCase();
      const kind = t.startsWith("image/") ? "image" : t.startsWith("audio/") ? "audio" : (t === "application/pdf" || n.endsWith(".pdf")) ? "pdf" : "other";
      setAtt({ attachment_path: data.path, attachment_name: f.name, attachment_kind: kind });
      toast.success("Fichier joint");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const submit = async () => {
    if (!text.trim() && !att) return toast.error("Message vide");
    setSending(true);
    try {
      const { data } = await api.post("/coach/messages/broadcast", { text: text.trim(), ...(att || {}) });
      toast.success(`Message envoyé à ${data.count} apprenant(s)`);
      setText(""); setAtt(null); setOpen(false); onDone?.();
    } catch { toast.error("Erreur"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="broadcast-button" className="dec-pill bg-[#1F2421] hover:bg-[#2A2F2B] text-white">
          <Megaphone className="w-4 h-4 mr-1" /> Message à tous
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle>Message à tous les apprenants</DialogTitle></DialogHeader>
        <p className="text-sm text-[#5C6561] -mt-2">Chaque apprenant actif le recevra dans sa propre messagerie.</p>
        <Textarea data-testid="broadcast-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Votre annonce…" className="bg-[#FAF8F5]" />
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" onChange={upload} className="hidden" accept="application/pdf,image/*,audio/*,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt" data-testid="broadcast-file-input" />
          <Button type="button" variant="outline" className="bg-white" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? <Upload className="w-4 h-4 mr-2 animate-pulse" /> : <Paperclip className="w-4 h-4 mr-2" />} Joindre un fichier
          </Button>
          {att && <span className="text-xs text-[#2A5B52] inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> {att.attachment_name}<button onClick={() => setAtt(null)}><X className="w-3 h-3" /></button></span>}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={sending} data-testid="broadcast-send" className="bg-[#D95D39] hover:bg-[#C04E2D] text-white">Envoyer à tous</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
